// ✅ src/pages/VehicleV2/MaintenanceList.jsx  (UPDATED FULL FILE)
// Backend base: https://nissi-india.com/api/maintenanceList
// Endpoints used:
//  - GET  /init?vehicleId=&branchId=
//  - GET  /subassemblies?vehicleId=
//  - GET  /descriptions?vehicleId=&subassemblyId=
//  - GET  /description-details?vehicleMainDetId=&vehicleId=
//  - GET  /hrkm?vehicleId=&date=dd/MM/yyyy
//  - GET  /list?vehicleId=&currentDate=dd/MM/yyyy
//  - POST /submit
//  - POST /delete
//  - POST /update-date
//  - POST /update-hrm
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + gridLoading + dropdownLoading + submitting + actionLoading states
// - safe init / form / grid / edit handling
// - consistent card / section / button style
// - cleaner validation and refresh handling

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSave,
  FaUndo,
  FaArrowLeft,
  FaTrash,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/maintenanceList";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toNum = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const isDDMMYYYY = (s) =>
  typeof s === "string" && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(s);

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const MaintenanceList = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // vehicleId can come from:
  // - URL: ?vehicleId=123
  // - localStorage: vid
  const vehicleId = useMemo(
    () => toInt(params.get("vehicleId")) || toInt(localStorage.getItem("vid")) || 0,
    [params]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [branchName, setBranchName] = useState("");
  const [vehicleInfo, setVehicleInfo] = useState(null);

  const [subassemblies, setSubassemblies] = useState([]);
  const [descriptions, setDescriptions] = useState([]);

  const [form, setForm] = useState({
    currentDate: todayDDMMYYYY(),
    subassemblyId: "",
    vehicleMainDetId: "",
    equipmentBasis: "",
    partNumber: "",
    quantity: "",
    specification: "",
    usageLimit: "",
    meterReading: "",
  });

  const [grid, setGrid] = useState([]);
  const [vehicleBasis, setVehicleBasis] = useState("");

  const [editing, setEditing] = useState({
    maintenanceId: null,
    changedDate: "",
    hrm: "",
    needsManualReading: false,
  });

  const isBusy =
    pageLoading || dropdownLoading || gridLoading || submitting || actionLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const disabledInputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg ${
        isDark
          ? "bg-gray-700/20 border-gray-600 text-gray-400"
          : "bg-gray-100 border-gray-300 text-gray-600"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const resetDescriptionFields = useCallback(() => {
    setForm((p) => ({
      ...p,
      vehicleMainDetId: "",
      equipmentBasis: "",
      partNumber: "",
      quantity: "",
      specification: "",
      usageLimit: "",
    }));
  }, []);

  const resetForm = useCallback(() => {
    setForm((p) => ({
      ...p,
      subassemblyId: "",
      vehicleMainDetId: "",
      equipmentBasis: "",
      partNumber: "",
      quantity: "",
      specification: "",
      usageLimit: "",
      meterReading: "",
    }));
    setDescriptions([]);
    toast.info("Form reset");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditing({
      maintenanceId: null,
      changedDate: "",
      hrm: "",
      needsManualReading: false,
    });
  }, []);

  /* =========================================================
     API LOADERS
     ========================================================= */
  const loadSubassemblies = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/subassemblies`, {
      params: { vehicleId },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load subassemblies");
    }

    setSubassemblies(data.list || []);
  }, [vehicleId, commonHeaders]);

  const loadDescriptions = useCallback(
    async (subassemblyId) => {
      if (!subassemblyId) {
        setDescriptions([]);
        return;
      }

      const { data } = await axios.get(`${API_BASE}/descriptions`, {
        params: { vehicleId, subassemblyId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load descriptions");
      }

      setDescriptions(data.list || []);
    },
    [vehicleId, commonHeaders]
  );

  const loadDescriptionDetails = useCallback(
    async (vehicleMainDetId) => {
      if (!vehicleMainDetId) return;

      const { data } = await axios.get(`${API_BASE}/description-details`, {
        params: { vehicleMainDetId, vehicleId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load description details");
      }

      const d = data.details || null;

      setForm((p) => ({
        ...p,
        equipmentBasis: d?.equipmentBasis || "",
        partNumber: d?.partNumber || "",
        quantity: d?.quantity || "",
        specification: d?.specification || "",
        usageLimit: d?.usageLimit || "",
      }));
    },
    [vehicleId, commonHeaders]
  );

  const loadHrKm = useCallback(
    async (dateStr) => {
      if (!isDDMMYYYY(dateStr)) return;

      const { data } = await axios.get(`${API_BASE}/hrkm`, {
        params: { vehicleId, date: dateStr },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.warn(data?.message || "No progress entry for selected date");
        setForm((p) => ({ ...p, meterReading: "" }));
        return;
      }

      setForm((p) => ({
        ...p,
        equipmentBasis: data.basis || p.equipmentBasis,
        meterReading: data.reading ?? "",
      }));
    },
    [vehicleId, commonHeaders]
  );

  const loadGrid = useCallback(
    async (dateStr) => {
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: { vehicleId, currentDate: dateStr },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load list");
      }

      setVehicleBasis(data.vehicleBasis || "");
      setGrid(data.list || []);
    },
    [vehicleId, commonHeaders]
  );

  const loadInit = useCallback(async () => {
    if (
      !canUse(
        vehicleId,
        "vehicleId missing. Pass ?vehicleId=123 or set localStorage 'vid'"
      )
    )
      return;

    if (
      !canUse(
        toInt(branchId),
        "branchId missing. Set localStorage 'branchId'/'branchid'"
      )
    )
      return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: { vehicleId, branchId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setBranchName(data.branchName || "");
      setVehicleInfo(data.vehicleInfo || null);

      const curDate = form.currentDate || todayDDMMYYYY();

      await Promise.all([
        loadSubassemblies(),
        loadHrKm(curDate),
        loadGrid(curDate),
      ]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Init error");
    } finally {
      setPageLoading(false);
    }
  }, [
    vehicleId,
    branchId,
    commonHeaders,
    canUse,
    form.currentDate,
    loadSubassemblies,
    loadHrKm,
    loadGrid,
  ]);

  /* ---------------- handlers ---------------- */
  const onChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  }, []);

  const onCurrentDateChange = useCallback(
    async (e) => {
      const value = e.target.value;
      setForm((p) => ({ ...p, currentDate: value }));

      if (!isDDMMYYYY(value)) return;

      try {
        setGridLoading(true);
        await loadHrKm(value);
        await loadGrid(value);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || err.message || "Date load error"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loadHrKm, loadGrid]
  );

  const onSubassemblyChange = useCallback(
    async (e) => {
      const subassemblyId = e.target.value;

      setForm((p) => ({
        ...p,
        subassemblyId,
        vehicleMainDetId: "",
        partNumber: "",
        quantity: "",
        specification: "",
        usageLimit: "",
      }));

      setDescriptions([]);

      if (!subassemblyId) return;

      try {
        setDropdownLoading(true);
        await loadDescriptions(subassemblyId);
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            "Load descriptions error"
        );
      } finally {
        setDropdownLoading(false);
      }
    },
    [loadDescriptions]
  );

  const onDescriptionChange = useCallback(
    async (e) => {
      const vehicleMainDetId = e.target.value;
      setForm((p) => ({ ...p, vehicleMainDetId }));

      if (!vehicleMainDetId) {
        resetDescriptionFields();
        return;
      }

      try {
        setDropdownLoading(true);
        await loadDescriptionDetails(vehicleMainDetId);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || err.message || "Load detail error"
        );
      } finally {
        setDropdownLoading(false);
      }
    },
    [loadDescriptionDetails, resetDescriptionFields]
  );

  const submit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;
    if (!canUse(vehicleId, "vehicleId missing")) return;
    if (!canUse(isDDMMYYYY(form.currentDate), "Current Date must be dd/MM/yyyy"))
      return;
    if (!canUse(form.subassemblyId, "Select Sub Assembly")) return;
    if (!canUse(form.vehicleMainDetId, "Select Description")) return;

    try {
      setSubmitting(true);

      const body = {
        vehicleId,
        branchId: toInt(branchId),
        loginUserId: toInt(loginUserId),
        currentDate: form.currentDate,
        subassemblyId: toInt(form.subassemblyId),
        vehicleMainDetId: toInt(form.vehicleMainDetId),
        meterReading: form.meterReading,
      };

      const { data } = await axios.post(`${API_BASE}/submit`, body, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Submit failed");
      }

      toast.success(data.message || "Saved");
      await loadGrid(form.currentDate);
      resetForm();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Submit error");
    } finally {
      setSubmitting(false);
    }
  }, [
    loginUserId,
    branchId,
    vehicleId,
    form,
    commonHeaders,
    canUse,
    loadGrid,
    resetForm,
  ]);

  const deleteRow = useCallback(
    async (maintenanceId) => {
      if (!maintenanceId) return;

      const ok = window.confirm("Delete this record?");
      if (!ok) return;

      try {
        setActionLoading(true);

        const { data } = await axios.post(
          `${API_BASE}/delete`,
          { maintenanceId },
          { headers: commonHeaders }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Delete failed");
        }

        toast.success("Deleted");
        await loadGrid(form.currentDate);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || err.message || "Delete error"
        );
      } finally {
        setActionLoading(false);
      }
    },
    [commonHeaders, loadGrid, form.currentDate]
  );

  const startEdit = useCallback(
    (row) => {
      setEditing({
        maintenanceId:
          row.fld_maintenanceid ??
          row.fld_maintenanceId ??
          row.maintenanceId ??
          null,
        changedDate: row.fld_curdate ? String(row.fld_curdate) : form.currentDate,
        hrm: row.fld_hrmtrreading ? String(row.fld_hrmtrreading) : "",
        needsManualReading: false,
      });
    },
    [form.currentDate]
  );

  const updateDate = useCallback(async () => {
    if (!editing.maintenanceId) return;
    if (!canUse(isDDMMYYYY(editing.changedDate), "Changed Date must be dd/MM/yyyy"))
      return;

    try {
      setActionLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/update-date`,
        {
          vehicleId,
          branchId: toInt(branchId),
          loginUserId: toInt(loginUserId),
          maintenanceId: editing.maintenanceId,
          changedDate: editing.changedDate,
        },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        toast.error(data?.message || "Update date failed");
        return;
      }

      if (data.needsManualReading) {
        toast.info(data.message || "Enter Hour/KM manually");
        setEditing((p) => ({ ...p, needsManualReading: true }));
        return;
      }

      toast.success("Updated");
      await loadGrid(form.currentDate);
      cancelEdit();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "Update date error"
      );
    } finally {
      setActionLoading(false);
    }
  }, [
    editing,
    vehicleId,
    branchId,
    loginUserId,
    commonHeaders,
    canUse,
    loadGrid,
    form.currentDate,
    cancelEdit,
  ]);

  const updateHrm = useCallback(async () => {
    if (!editing.maintenanceId) return;
    if (!canUse(isDDMMYYYY(editing.changedDate), "Changed Date must be dd/MM/yyyy"))
      return;
    if (
      !canUse(
        editing.hrm && !Number.isNaN(Number(editing.hrm)),
        "Hour/KM reading must be numeric"
      )
    )
      return;

    try {
      setActionLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/update-hrm`,
        {
          vehicleId,
          branchId: toInt(branchId),
          loginUserId: toInt(loginUserId),
          maintenanceId: editing.maintenanceId,
          changedDate: editing.changedDate,
          hrm: editing.hrm,
        },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Update HRM failed");
      }

      toast.success("Updated");
      await loadGrid(form.currentDate);
      cancelEdit();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "Update HRM error"
      );
    } finally {
      setActionLoading(false);
    }
  }, [
    editing,
    vehicleId,
    branchId,
    loginUserId,
    commonHeaders,
    canUse,
    loadGrid,
    form.currentDate,
    cancelEdit,
  ]);

  const back = useCallback(() => {
    navigate("/vehicle/PeriodEntryListing");
  }, [navigate]);

  const refreshAll = useCallback(async () => {
    await loadInit();
  }, [loadInit]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">MAINTENANCE LIST</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Maintenance List
              </div>

              <div
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Vehicle: {vehicleInfo?.vehicleCode || ""}{" "}
                {vehicleInfo?.vehicleRegNo
                  ? `(${vehicleInfo.vehicleRegNo})`
                  : ""}
                {" • "}Branch: {branchName || ""}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Vehicle ID: {vehicleId || "-"}</span>
                <span className={pillClass}>
                  Records: {grid.length || 0}
                </span>
                <span className={pillClass}>
                  Basis: {vehicleBasis || "-"}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={back}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button
                onClick={refreshAll}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                title="Refresh"
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* TOP FORM */}
          <div className={`${sectionClass}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* left */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Equipment Code</label>
                  <input
                    value={vehicleInfo?.vehicleCode || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Sub Assembly <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="subassemblyId"
                    value={form.subassemblyId}
                    onChange={onSubassemblyChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {subassemblies.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Equipment Basis</label>
                  <input
                    value={form.equipmentBasis || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Specification</label>
                  <input
                    value={form.specification || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Quantity</label>
                  <input
                    value={form.quantity || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Current Date (dd/MM/yyyy)</label>
                  <input
                    name="currentDate"
                    value={form.currentDate}
                    onChange={onCurrentDateChange}
                    className={inputClass}
                    placeholder="dd/MM/yyyy"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Equipment Type</label>
                  <input
                    value={vehicleInfo?.vehicleName || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>
              </div>

              {/* right */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Branch Name</label>
                  <input
                    value={branchName || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Description <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="vehicleMainDetId"
                    value={form.vehicleMainDetId}
                    onChange={onDescriptionChange}
                    className={inputClass}
                    disabled={!form.subassemblyId || isBusy}
                  >
                    <option value="">Select</option>
                    {descriptions.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Part Number / Oil Grade</label>
                  <input
                    value={form.partNumber || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Usage Limit</label>
                  <input
                    value={form.usageLimit || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Hour/Kilo Meter Reading</label>
                  <input
                    name="meterReading"
                    value={form.meterReading}
                    onChange={onChange}
                    className={inputClass}
                    placeholder="Auto-filled if available"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Equipment Number</label>
                  <input
                    value={vehicleInfo?.vehicleCode || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Equipment Name</label>
                  <input
                    value={vehicleInfo?.vehicleRegNo || ""}
                    disabled
                    className={disabledInputClass}
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col md:flex-row gap-3 justify-end mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={submit}
                disabled={isBusy}
                className={`flex items-center justify-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
              >
                <FaSave />
                {submitting ? "Submitting..." : "Submit"}
              </button>

              <button
                onClick={resetForm}
                type="button"
                disabled={isBusy}
                className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
              >
                <FaUndo />
                Reset
              </button>
            </div>
          </div>

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-800"
                }`}
              >
                Periodic List
              </div>
              <div
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Basis: <span className="font-semibold">{vehicleBasis || "-"}</span>
                {" • "}
                Records: <span className="font-semibold">{grid.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead
                  className={isDark ? "bg-gray-800 text-gray-200" : "bg-gray-50 text-gray-700"}
                >
                  <tr>
                    <th className="px-3 py-3 text-left">S.No</th>
                    <th className="px-3 py-3 text-left">Maintenance ID</th>
                    <th className="px-3 py-3 text-left">Description</th>
                    <th className="px-3 py-3 text-left">Part No</th>
                    <th className="px-3 py-3 text-left">Limit</th>
                    <th className="px-3 py-3 text-left">Reading</th>
                    <th className="px-3 py-3 text-left">Current HR/KM</th>
                    <th className="px-3 py-3 text-left">Remaining</th>
                    <th className="px-3 py-3 text-left">Status</th>
                    <th className="px-3 py-3 text-left">Actions</th>
                  </tr>
                </thead>

                <tbody
                  className={isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"}
                >
                  {grid.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                        {gridLoading || pageLoading ? "Loading..." : "No records"}
                      </td>
                    </tr>
                  ) : (
                    grid.map((r, idx) => {
                      const maintenanceId =
                        r.fld_maintenanceid ??
                        r.fld_maintenanceId ??
                        r.maintenanceId ??
                        "";

                      const desc = r.fld_Description ?? r.description ?? "";
                      const part = r.fld_Partnumber ?? r.partNumber ?? "";
                      const limit = r.fld_usagelimit ?? "";
                      const reading = r.fld_hrmtrreading ?? "";
                      const current = r.fld_hrskm ?? "";
                      const remaining = r.fld_Remaininghr ?? "";
                      const status = r.serviceStatus ?? "";

                      const isEditing = editing.maintenanceId === maintenanceId;

                      return (
                        <tr
                          key={`${maintenanceId}-${idx}`}
                          className={isDark ? "border-t border-gray-800" : "border-t border-gray-100"}
                        >
                          <td className="px-3 py-2">{idx + 1}</td>
                          <td className="px-3 py-2">{maintenanceId}</td>
                          <td className="px-3 py-2">{desc}</td>
                          <td className="px-3 py-2">{part}</td>
                          <td className="px-3 py-2">{limit}</td>
                          <td className="px-3 py-2">{reading}</td>
                          <td className="px-3 py-2">{current}</td>
                          <td className="px-3 py-2">{remaining}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`px-2 py-1 rounded-md text-xs font-semibold ${
                                status === "Service Expired"
                                  ? "bg-red-600 text-white"
                                  : status === "Service Nearly"
                                  ? "bg-yellow-500 text-black"
                                  : isDark
                                  ? "bg-gray-800 text-gray-200"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {status || "-"}
                            </span>
                          </td>

                          <td className="px-3 py-2">
                            {!isEditing ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => startEdit(r)}
                                  disabled={isBusy}
                                  className={`px-3 py-1 rounded-md text-white ${buttonClass} ${hoverClass} ${
                                    isBusy ? "opacity-60 cursor-not-allowed" : ""
                                  }`}
                                  title="Edit changed date"
                                >
                                  Edit
                                </button>

                                <button
                                  onClick={() => deleteRow(maintenanceId)}
                                  disabled={isBusy}
                                  className={`px-3 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white ${
                                    isBusy ? "opacity-60 cursor-not-allowed" : ""
                                  }`}
                                  title="Delete"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <input
                                    value={editing.changedDate}
                                    onChange={(e) =>
                                      setEditing((p) => ({
                                        ...p,
                                        changedDate: e.target.value,
                                      }))
                                    }
                                    className={inputClass}
                                    placeholder="dd/MM/yyyy"
                                    disabled={isBusy}
                                  />

                                  <button
                                    onClick={updateDate}
                                    disabled={isBusy}
                                    className={`px-3 py-2 rounded-md text-white ${buttonClass} ${hoverClass} disabled:opacity-60`}
                                  >
                                    Update Date
                                  </button>

                                  <button
                                    onClick={cancelEdit}
                                    disabled={isBusy}
                                    className={`px-3 py-2 rounded-md ${
                                      isDark
                                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                                        : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                                  >
                                    Cancel
                                  </button>
                                </div>

                                {editing.needsManualReading && (
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                    <input
                                      value={editing.hrm}
                                      onChange={(e) =>
                                        setEditing((p) => ({
                                          ...p,
                                          hrm: e.target.value,
                                        }))
                                      }
                                      className={inputClass}
                                      placeholder="Enter Hour/KM reading"
                                      disabled={isBusy}
                                    />

                                    <button
                                      onClick={updateHrm}
                                      disabled={isBusy}
                                      className="px-3 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
                                    >
                                      Update Reading
                                    </button>

                                    <div />
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {isBusy && (
              <div
                className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
              >
                {pageLoading
                  ? "Loading page..."
                  : dropdownLoading
                  ? "Loading dropdown..."
                  : gridLoading
                  ? "Loading grid..."
                  : submitting
                  ? "Submitting..."
                  : actionLoading
                  ? "Processing..."
                  : "Loading..."}
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default MaintenanceList;